import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Box, TextField, Button, Typography,
  InputLabel, Select, MenuItem, FormControl,
} from '@mui/material';
import DTTagSelector from './DTTagSelector';

const FONT_TYPES = ['Roboto', 'Arial', 'Verdana', 'Courier New', 'Georgia'];
const SCROLL_MODES = [
  { value: 'scrollable', label: 'Scrollable — content scrolls within the cell' },
  { value: 'fixed',      label: 'Fixed — content clips to cell height' },
];

/** Shared sx helpers for themed form fields */
function themedTextField(isDark, extra = {}) {
  return {
    mb: 1.5,
    ...extra,
    '& .MuiInputBase-input': { color: isDark ? '#fff' : '#000' },
    '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#555' },
    '& .MuiInputLabel-root.Mui-focused': { color: isDark ? '#90caf9' : undefined },
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? '#252525' : '#fafafa',
      '& fieldset': { borderColor: isDark ? '#444' : '#ccc' },
      '&:hover fieldset': { borderColor: isDark ? '#666' : '#999' },
      '&.Mui-focused fieldset': { borderColor: isDark ? '#90caf9' : undefined },
    },
  };
}

function themedSelectSx(isDark) {
  return {
    mb: 1.5,
    '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#555' },
    '& .MuiInputLabel-root.Mui-focused': { color: isDark ? '#90caf9' : undefined },
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? '#252525' : '#fafafa',
      color: isDark ? '#fff' : '#000',
      '& fieldset': { borderColor: isDark ? '#444' : '#ccc' },
      '&:hover fieldset': { borderColor: isDark ? '#666' : '#999' },
      '&.Mui-focused fieldset': { borderColor: isDark ? '#90caf9' : undefined },
    },
    '& .MuiSvgIcon-root': { color: isDark ? '#aaa' : undefined },
  };
}

/** Reusable themed <Select> builder */
function ThemedSelect({ label, value, options, onChange, isDark }) {
  const menuPaperSx = {
    backgroundColor: isDark ? '#252525' : '#fff',
    color: isDark ? '#fff' : '#000',
    '& .MuiMenuItem-root:hover': { backgroundColor: isDark ? '#333' : '#f5f5f5' },
  };
  return (
    <FormControl size="small" fullWidth sx={themedSelectSx(isDark)}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        MenuProps={{ PaperProps: { sx: menuPaperSx } }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ color: isDark ? '#fff' : '#000' }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function BasicPropsTab({ basic, onChange, isDark }) {
  const field = (label, key, type = 'text') => (
    <TextField
      key={key}
      label={label}
      value={basic[key] ?? ''}
      onChange={(e) => onChange({ ...basic, [key]: type === 'number' ? Number(e.target.value) : e.target.value })}
      size="small"
      fullWidth
      type={type}
      sx={themedTextField(isDark)}
    />
  );

  return (
    <Box sx={{ pt: 1 }}>
      {field('Widget Name', 'widgetName')}
      {field('Font Size', 'fontSize', 'number')}
      <ThemedSelect
        label="Font Type"
        value={basic.fontType || 'Roboto'}
        options={FONT_TYPES.map((f) => ({ value: f, label: f }))}
        onChange={(v) => onChange({ ...basic, fontType: v })}
        isDark={isDark}
      />
      <ThemedSelect
        label="Content Display"
        value={basic.scrollMode || 'scrollable'}
        options={SCROLL_MODES}
        onChange={(v) => onChange({ ...basic, scrollMode: v })}
        isDark={isDark}
      />
      {field('Border Size (px)', 'borderSize', 'number')}
      {field('Border Radius (px)', 'borderRadius', 'number')}
    </Box>
  );
}

function ExtendedPropsTab({ widget, extended, onChange, theme }) {
  if (!widget) return null;
  return (
    <Box sx={{ pt: 1 }}>
      <DTTagSelector
        widgetType={widget.type}
        selectedTags={extended.tagprops || []}
        onTagsChange={(tagprops) => onChange({ ...extended, tagprops })}
        theme={theme}
      />
    </Box>
  );
}

function DTWidgetForm({ open, widget, onSave, onClose, theme }) {
  const isDark = theme === 'dark';
  const [tab, setTab] = useState(0);
  const [basic, setBasic] = useState({});
  const [extended, setExtended] = useState({});

  useEffect(() => {
    if (widget) {
      setBasic(widget.props?.basic || {});
      setExtended(widget.props?.extended || {});
      setTab(0);
    }
  }, [widget]);

  if (!widget) return null;

  const handleSave = () => {
    onSave({ ...widget, props: { basic, extended } });
  };

  const bgMain = isDark ? '#1a1a1a' : '#ffffff';
  const bgSurface = isDark ? '#242424' : '#fafafa';
  const textMain = isDark ? '#ffffff' : '#000000';
  const borderColor = isDark ? '#333' : '#e0e0e0';
  const mutedColor = isDark ? '#aaa' : '#555';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: bgMain,
          color: textMain,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ color: textMain, pb: 0, borderBottom: `1px solid ${borderColor}` }}>
        Configure Widget{' '}
        <Typography component="span" variant="body2" sx={{ color: mutedColor }}>
          — {widget?.type}
        </Typography>
      </DialogTitle>

      <Box sx={{ borderBottom: `1px solid ${borderColor}`, px: 3, backgroundColor: bgMain }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: '#008CD7' } }}
        >
          <Tab
            label="Appearance"
            sx={{ textTransform: 'none', fontSize: 13, color: mutedColor, '&.Mui-selected': { color: textMain } }}
          />
          <Tab
            label="Configuration"
            sx={{ textTransform: 'none', fontSize: 13, color: mutedColor, '&.Mui-selected': { color: textMain } }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 1.5, backgroundColor: bgSurface }}>
        {tab === 0 && <BasicPropsTab basic={basic} onChange={setBasic} isDark={isDark} />}
        {tab === 1 && (
          <ExtendedPropsTab widget={widget} extended={extended} onChange={setExtended} theme={theme} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${borderColor}`, backgroundColor: bgMain }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: mutedColor }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" sx={{ textTransform: 'none' }}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default React.memo(DTWidgetForm);
